import React from 'react';
import { Check, Gift } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { vehicles } from '../data/mock';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';

const Pricing = () => {
  const { t } = useLanguage();

  const getVehicleData = (vehicleType) => {
    return t.vehicles[vehicleType];
  };

  return (
    <section id="pricing" className="py-20 bg-gradient-to-br from-[#0077FF] to-[#0055CC] text-white relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)',
          backgroundSize: '30px 30px'
        }}></div>
      </div>

      <div className="container mx-auto px-4 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold mb-4">
            {t.pricing.title}
          </h2>
          <p className="text-lg text-white/90 max-w-2xl mx-auto">
            {t.pricing.subtitle}
          </p>
        </div>

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto mb-12">
          {vehicles.map((vehicle, index) => {
            const vehicleData = getVehicleData(vehicle.type);
            const isPopular = index === 1; // Middle card is popular

            return (
              <Card 
                key={vehicle.id} 
                className={`overflow-hidden bg-white text-gray-900 hover:shadow-2xl transition-all duration-300 ${
                  isPopular ? 'transform scale-105 border-4 border-[#FF8C00]' : 'hover:scale-105'
                }`}
              >
                {isPopular && (
                  <div className="bg-gradient-to-r from-[#FF8C00] to-[#FF6B35] text-white text-center py-2 font-bold text-sm">
                    ⭐ POPULAIRE
                  </div>
                )}

                <CardHeader className="text-center pb-4">
                  <CardTitle className="text-3xl font-bold mb-2">
                    {vehicleData.name}
                  </CardTitle>
                  <div className="mt-4">
                    <div className="text-5xl font-bold text-[#0077FF]">
                      {vehicle.priceFormatted.split(' ')[0]} {vehicle.priceFormatted.split(' ')[1]}
                    </div>
                    <div className="text-gray-600 font-medium mt-1">
                      XPF {t.pricing.perDay}
                    </div>
                  </div>
                </CardHeader>

                <CardContent className="space-y-3 px-6">
                  {vehicleData.features.map((feature, idx) => (
                    <div key={idx} className="flex items-start space-x-2">
                      <Check className="w-5 h-5 text-[#0077FF] mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700 text-sm">{feature}</span>
                    </div>
                  ))}
                </CardContent>

                <CardFooter className="px-6 pb-6">
                  <Button 
                    className={`w-full font-semibold py-6 md:py-4 text-base md:text-sm ${
                      isPopular 
                        ? 'bg-gradient-to-r from-[#FF8C00] to-[#FF6B35] hover:from-[#FF6B35] hover:to-[#FF8C00]' 
                        : 'bg-gradient-to-r from-[#0077FF] to-[#0055CC] hover:from-[#0055CC] hover:to-[#0077FF]'
                    } text-white`}
                    onClick={() => {
                      const element = document.getElementById('contact');
                      if (element) element.scrollIntoView({ behavior: 'smooth' });
                    }}
                  >
                    {t.pricing.bookNow}
                  </Button>
                </CardFooter>
              </Card>
            );
          })}
        </div>

        {/* Promotion Banner */}
        <div className="max-w-3xl mx-auto">
          <div className="bg-gradient-to-r from-[#FF8C00] to-[#FF6B35] rounded-2xl p-8 shadow-2xl border-4 border-white/20">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="flex items-center space-x-4">
                <div className="bg-white/20 p-4 rounded-full">
                  <Gift className="w-8 h-8 text-white" />
                </div>
                <div className="text-center md:text-left">
                  <div className="text-2xl md:text-3xl font-bold">
                    {t.pricing.promotion}
                  </div>
                  <div className="text-sm text-white/90 mt-1">
                    Économisez sur vos locations longue durée
                  </div>
                </div>
              </div>
              <Button 
                size="lg"
                className="bg-white text-[#FF8C00] hover:bg-gray-100 font-bold px-8 shadow-lg"
                onClick={() => {
                  const element = document.getElementById('contact');
                  if (element) element.scrollIntoView({ behavior: 'smooth' });
                }}
              >
                Profiter
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Pricing;
